import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';

const SplashScreen = () => {
  const [animationPhase, setAnimationPhase] = useState('loading'); // loading -> loaded -> fadeOut -> welcome
  const navigate = useNavigate();

  useEffect(() => {
    const timer1 = setTimeout(() => {
      setAnimationPhase('loaded');
    }, 1000);

    const timer2 = setTimeout(() => {
      setAnimationPhase('fadeOut');
    }, 2500);

    const timer3 = setTimeout(() => {
      // Redirection directe vers /home (plus de dépendance à useAuth)
      setAnimationPhase('welcome');
    }, 3500);

    // Timeout de sécurité: redirection forcée après 8 secondes
    const fallbackTimer = setTimeout(() => {
      console.log('SplashScreen: Redirection forcée vers /home');
      navigate('/home');
    }, 8000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(fallbackTimer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-violet-900/20" />
        
        {/* Moving particles */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-pink-400/30 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}
        </div>

        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(236,72,153,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(236,72,153,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center">
        {/* Animation principale (logo + loading) */}
        {animationPhase !== 'welcome' && (
          <>
            {/* Logo container */}
            <div 
              className={`transition-all duration-1000 ease-out ${
                animationPhase === 'loading' 
                  ? 'opacity-0 transform scale-75 translate-y-8' 
                  : animationPhase === 'loaded'
                  ? 'opacity-100 transform scale-100 translate-y-0'
                  : 'opacity-0 transform scale-110 translate-y-[-8px]'
              }`}
            >
              {/* Logo */}
              <div className="mb-8 flex justify-center">
                <div className="relative">
                  {/* Glow effect behind logo */}
                  <div className="absolute inset-0 blur-xl bg-pink-500/20 rounded-full scale-150" />
                  
                  <img
                    src="https://customer-assets.emergentagent.com/job_emergent-ai-stream/artifacts/5inqwv3t_Logo%20IA%20Watch.png"
                    alt="YUUME"
                    className="w-32 h-32 md:w-40 md:h-40 relative z-10 drop-shadow-2xl"
                  />
                  
                  {/* Rotating ring around logo */}
                  <div className="absolute inset-0 border-2 border-pink-500/30 rounded-full animate-spin" style={{ animationDuration: '3s' }} />
                  <div className="absolute inset-2 border border-violet-500/20 rounded-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />
                </div>
              </div>

              {/* Brand name */}
              <h1 className="text-4xl md:text-6xl font-bold mb-4">
                <span className="bg-gradient-to-r from-pink-400 via-pink-500 to-violet-500 bg-clip-text text-transparent animate-pulse">
                  YUUME
                </span>
              </h1>

              {/* Tagline */}
              <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-md mx-auto">
                L'avenir du cinéma généré par intelligence artificielle
              </p>

              {/* Animated sparkles */}
              <div className="flex justify-center space-x-4 mb-8">
                {[...Array(3)].map((_, i) => (
                  <Sparkles
                    key={i}
                    className="h-6 w-6 text-pink-400 animate-bounce"
                    style={{
                      animationDelay: `${i * 0.2}s`,
                      animationDuration: '2s'
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Loading indicator */}
            <div 
              className={`transition-all duration-500 ${
                animationPhase === 'fadeOut' ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {/* Progress bar */}
              <div className="w-64 h-1 bg-gray-800 rounded-full mx-auto mb-4 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-pink-500 to-violet-500 rounded-full transition-all duration-2000 ease-out"
                  style={{
                    width: animationPhase === 'loading' ? '0%' : '100%'
                  }}
                />
              </div>

              {/* Loading text */}
              <p className="text-gray-400 text-sm animate-pulse">
                {animationPhase === 'loading' 
                  ? 'Initialisation...' 
                  : 'Chargement terminé'
                }
              </p>
            </div>
          </>
        )}

        {/* Écran de bienvenue avec boutons d'action */}
        {animationPhase === 'welcome' && (
          <div className="animate-fade-in-up">
            {/* Logo plus petit */}
            <div className="mb-8 flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_emergent-ai-stream/artifacts/5inqwv3t_Logo%20IA%20Watch.png"
                alt="YUUME"
                className="w-24 h-24 drop-shadow-2xl"
              />
            </div>

            {/* Titre de bienvenue */}
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-pink-400 via-pink-500 to-violet-500 bg-clip-text text-transparent">
                YUUME
              </span>
            </h1>

            <h2 className="text-xl md:text-2xl text-white font-semibold mb-4">
              Bienvenue dans l'avenir du cinéma
            </h2>

            <p className="text-gray-300 mb-12 max-w-lg mx-auto text-lg">
              Découvrez des films et séries exclusifs entièrement générés par intelligence artificielle. 
              Une expérience cinématographique révolutionnaire vous attend.
            </p>

            {/* Boutons d'action */}
            <div className="space-y-4 max-w-sm mx-auto">
              <button
                onClick={() => navigate('/signup')}
                className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-pink-600 to-violet-600 hover:from-pink-700 hover:to-violet-700 text-white px-8 py-4 rounded-full transition-all duration-300 font-semibold text-lg shadow-lg shadow-pink-600/30 hover:shadow-pink-600/50 transform hover:scale-105"
              >
                <span>Commencer l'aventure</span>
                <ArrowRight className="h-5 w-5" />
              </button>

              <button
                onClick={() => navigate('/signin')}
                className="w-full flex items-center justify-center space-x-3 bg-transparent border-2 border-gray-600 hover:border-pink-500 text-gray-300 hover:text-white px-8 py-4 rounded-full transition-all duration-300 font-semibold"
              >
                <span>J'ai déjà un compte</span>
              </button>

              {/* Lien pour continuer sans compte (browse) */}
              <button
                onClick={() => navigate('/home')}
                className="text-gray-500 hover:text-gray-300 text-sm transition-colors mt-6"
              >
                Continuer sans compte →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom decoration */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex space-x-2">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"
              style={{
                animationDelay: `${i * 0.3}s`,
                animationDuration: '1.5s'
              }}
            />
          ))}
        </div>
      </div>

      {/* Click to skip (après l'animation principale) */}
      {animationPhase !== 'loading' && animationPhase !== 'welcome' && (
        <button
          onClick={() => {
            // Redirection directe vers la page de bienvenue
            setAnimationPhase('welcome');
          }}
          className="absolute bottom-4 right-4 text-gray-500 hover:text-gray-300 text-sm transition-colors"
        >
          Cliquer pour continuer →
        </button>
      )}
    </div>
  );
};

export default SplashScreen;